/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { put, takeLatest } from "redux-saga/effects";

import { ExcalidrawElement } from "@excalidraw/excalidraw/types/element/types";
import { BinaryFiles } from "@excalidraw/excalidraw/types/types";
import { IInitialState } from "@/types/initialState.types";
import {
  IMergeAndUpdateCodeAction,
  IMergeAndUpdateDrawingAction,
} from "@/types/collabTemporaryData.types";
import { reconcileElements } from "@/utils/Whiteboard.utils";
import { getServerTime } from "@/utils/utils";
import { IMergeAndUpdateSubjectiveContentAction } from "@/types/subjectiveCollab.types";
import {
  DELETE_SINGLE_CODE_CHUNK_FOR_USER_FOR_QUESTION,
  MERGE_AND_UPDATE_CODE,
  UPDATE_CODE,
  DELETE_SINGLE_DRAWING_CHUNK_FOR_USER_FOR_QUESTION,
  MERGE_AND_UPDATE_DRAWING,
  MERGE_AND_UPDATE_SUBJECTIVE_CONTENT,
  DELETE_SINGLE_SUBJECTIVE_CHUNK_FOR_USER_FOR_QUESTION,
  SET_SUBJECTIVE_SOLUTION,
  CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION,
} from "../constants/ActionTypes";
import { select } from "./utils.saga";

/**
 * merging and updating code sync data
 * We took code sync data received from user via email for a question
 * We are checking if all chunks are there via isCompleted flag on data
 * here we checking and comparing for codeSyncData languaheModifiedAt with solution
 * to ensure only latest data will be updated in code
 * then we are parsing the data.
 * then we are decoding the code
 * if language is changed we are changing language and code.
 * if only code is changed we are updating code only
 */
function* mergeAndUpdateCode({ payload }: IMergeAndUpdateCodeAction) {
  const { email, questionSlug } = payload;
  const { codeSyncData, solution } = yield* select((state: IInitialState) => ({
    solution: state.solutions.allSolutions[questionSlug],
    codeSyncData: state.collabTemporaryData.codeSyncData[questionSlug],
  }));
  const currentEmailSyncData = codeSyncData[email];
  if (
    currentEmailSyncData &&
    currentEmailSyncData.isCompleted &&
    ((currentEmailSyncData.languageModifiedAt &&
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      !solution?.languageModifiedAt) ||
      (currentEmailSyncData.codeModifiedAt && !solution?.codeModifiedAt) ||
      (solution?.languageModifiedAt &&
        currentEmailSyncData.languageModifiedAt &&
        new Date(currentEmailSyncData.languageModifiedAt) >
          new Date(solution.languageModifiedAt)) ||
      (solution?.codeModifiedAt &&
        currentEmailSyncData.codeModifiedAt &&
        new Date(currentEmailSyncData.codeModifiedAt) >
          new Date(solution.codeModifiedAt)))
  ) {
    let stringifyCompressedCode = "";
    const { length } = Object.values(currentEmailSyncData.codeChunkData);
    for (let i = 1; i <= length; i += 1) {
      if (!currentEmailSyncData.codeChunkData[i]) {
        stringifyCompressedCode = "";
        break;
      }
      stringifyCompressedCode += currentEmailSyncData.codeChunkData[i].code;
    }
    if (stringifyCompressedCode) {
      const code = JSON.parse(stringifyCompressedCode) as string;
      if (
        !solution ||
        (!solution?.languageModifiedAt &&
          currentEmailSyncData.languageModifiedAt) ||
        (solution &&
          solution.technology !== currentEmailSyncData.language &&
          currentEmailSyncData.languageModifiedAt &&
          solution.languageModifiedAt &&
          new Date(currentEmailSyncData.languageModifiedAt) >
            new Date(solution.languageModifiedAt))
      ) {
        yield put({
          type: `${MERGE_AND_UPDATE_CODE}_SUCCESS`,
          payload: {
            questionSlug,
            code,
            technology: currentEmailSyncData.language,
            languageModifiedAt: currentEmailSyncData.languageModifiedAt,
            codeModifiedAt: currentEmailSyncData.codeModifiedAt,
          },
        });
      } else if (solution.technology === currentEmailSyncData.language) {
        yield put({
          type: UPDATE_CODE,
          payload: {
            questionSlug,
            code,
          },
        });
      }
    }
  }
  yield put({
    type: DELETE_SINGLE_CODE_CHUNK_FOR_USER_FOR_QUESTION,
    payload: {
      questionSlug,
      userEmail: email,
    },
  });
}
/*
 * merging and updating drawing sync data
 * We took drawing sync data received from user via email for a question
 * We are checking if all chunks are there via isCompleted flag on data
 * here we checking and comparing for drawingSyncData modifiedAt with solution
 * to ensure only latest data will be updated in drawing
 * then we are parsing the data.
 * then we are decoding the drawing the data
 * If solution already has elements we are reconciling elements to get only updated elements.
 * And updating it in the state.
 */

function* mergeAndUpdateDrawing({ payload }: IMergeAndUpdateDrawingAction) {
  const { email, questionSlug } = payload;
  const { drawingSyncData, solution, serverTimeOffsetTime } = yield* select(
    (state: IInitialState) => ({
      solution: state.solutions.whiteboardSolutions[questionSlug],
      drawingSyncData: state.collabTemporaryData.drawingSyncData[questionSlug],
      serverTimeOffsetTime: state.serverTime.offsetTime,
    }),
  );
  const currentDrawingSyncData = drawingSyncData[email];
  if (
    currentDrawingSyncData.isCompleted &&
    ((currentDrawingSyncData.modifiedAt && !solution?.modifiedAt) ||
      (currentDrawingSyncData.modifiedAt &&
        solution?.modifiedAt &&
        new Date(currentDrawingSyncData.modifiedAt) >= solution.modifiedAt))
  ) {
    let stringifyDrawingData = "";
    const { length } = Object.values(currentDrawingSyncData.drawingChunkData);
    for (let i = 1; i <= length; i += 1) {
      if (!currentDrawingSyncData.drawingChunkData[i]) {
        stringifyDrawingData = "";
        break;
      }
      stringifyDrawingData +=
        currentDrawingSyncData.drawingChunkData[i].drawingData;
    }
    if (stringifyDrawingData) {
      const encodedDrawingData = JSON.parse(stringifyDrawingData) as string;
      const drawingDataInObj = JSON.parse(encodedDrawingData) as {
        elements: string;
        files: string;
      };
      let elements = JSON.parse(
        drawingDataInObj.elements,
      ) as readonly ExcalidrawElement[];
      const files = JSON.parse(drawingDataInObj.files) as BinaryFiles;
      if (solution?.elements) {
        elements = reconcileElements(solution.elements, elements);
      }
      yield put({
        type: `${MERGE_AND_UPDATE_DRAWING}_SUCCESS`,
        payload: {
          elements,
          files,
          questionSlug,
          modifiedAt: getServerTime(serverTimeOffsetTime),
        },
      });
    }
  }
  yield put({
    type: DELETE_SINGLE_DRAWING_CHUNK_FOR_USER_FOR_QUESTION,
    payload: {
      questionSlug,
      userEmail: email,
    },
  });
}

function* mergeAndUpdateSubjectiveContent({
  payload,
}: IMergeAndUpdateSubjectiveContentAction) {
  const { email, questionSlug } = payload;
  const { subjectiveSyncData, solution, serverTimeOffsetTime, interviewSlug } =
    yield* select((state: IInitialState) => ({
      solution: state.solutions.subjectiveSolutions[questionSlug],
      subjectiveSyncData:
        state.collabTemporaryData.subjectiveSyncData[questionSlug],
      serverTimeOffsetTime: state.serverTime.offsetTime,
      interviewSlug: state.interview?.slug,
    }));

  const currentSyncData = subjectiveSyncData?.[email];

  if (
    currentSyncData?.isCompleted &&
    ((currentSyncData.modifiedAt && !solution?.modifiedAt) ||
      (currentSyncData.modifiedAt &&
        solution?.modifiedAt &&
        new Date(currentSyncData.modifiedAt) >= solution.modifiedAt))
  ) {
    let stringifyContentData = "";
    const { length } = Object.values(currentSyncData.contentChunkData);
    for (let i = 1; i <= length; i += 1) {
      if (!currentSyncData.contentChunkData[i]) {
        stringifyContentData = "";
        break;
      }
      stringifyContentData += currentSyncData.contentChunkData[i].contentData;
    }

    if (stringifyContentData) {
      const content = JSON.parse(stringifyContentData) as string;
      yield put({
        type: SET_SUBJECTIVE_SOLUTION,
        payload: {
          questionSlug,
          solution: {
            slug: solution?.slug,
            content,
            attachments: solution?.attachments ?? [],
            modifiedAt: getServerTime(serverTimeOffsetTime),
          },
        },
      });
      if (interviewSlug) {
        yield put({
          type: CREATE_OR_UPDATE_SUBJECTIVE_SOLUTION,
          payload: { interviewSlug, questionSlug },
        });
      }
    }
  }

  yield put({
    type: DELETE_SINGLE_SUBJECTIVE_CHUNK_FOR_USER_FOR_QUESTION,
    payload: { questionSlug, userEmail: email },
  });
}

export default function* loadCollabTemporaryDataSaga() {
  yield takeLatest(MERGE_AND_UPDATE_CODE, mergeAndUpdateCode);
  yield takeLatest(MERGE_AND_UPDATE_DRAWING, mergeAndUpdateDrawing);
  yield takeLatest(
    MERGE_AND_UPDATE_SUBJECTIVE_CONTENT,
    mergeAndUpdateSubjectiveContent,
  );
}
